import { useEffect, useMemo, useState } from 'react'
import { useLocation, useRouter } from '@tanstack/react-router'

import { LoginScreen, type LoginFormValues } from '@/screens/LoginScreen'
import { useAuth } from '@/lib/auth'
import { useBranding } from '@/lib/branding'
import { useDb } from '@/lib/dbContext'
import { withSpan } from '@/lib/sentry'

export function LoginScreenContainer() {
  const router = useRouter()
  const location = useLocation()
  const db = useDb()
  const brand = useBranding()
  const { login, isAuthenticated, user } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const isLoginRoute = location.pathname === '/login'
  const kioskName = useMemo(() => {
    let kiosk: string | null = null
    try {
      kiosk = new URL(window.location.href).searchParams.get('kiosk')
    } catch {
      kiosk = null
    }
    if (!kiosk && location.search && typeof location.search === 'object') {
      const value = (location.search as { kiosk?: unknown }).kiosk
      kiosk = typeof value === 'string' ? value : null
    }
    return kiosk
  }, [location.search])

  const isKioskLogin = Boolean(kioskName)

  const getRedirectTarget = () => {
    let redirect: string | null = null
    try {
      redirect = new URL(window.location.href).searchParams.get('redirect')
    } catch {
      redirect = null
    }
    if (!redirect && location.search && typeof location.search === 'object') {
      const value = (location.search as { redirect?: unknown }).redirect
      redirect = typeof value === 'string' ? value : null
    }
    if (!redirect || !redirect.startsWith('/') || redirect.startsWith('/login')) {
      redirect = '/bookings'
    }
    return redirect
  }

  const handleSubmit = async (values: LoginFormValues) => {
    setErrorMessage(null)
    setIsSubmitting(true)
    try {
      await withSpan(
        {
          op: 'auth.login',
          name: 'Login submit',
        },
        () => login(values.email, values.password),
      )
      const redirectTarget = getRedirectTarget()
      if (kioskName && db.authStore.token) {
        router.navigate({ to: '/kiosk-control/pair', search: { kiosk: kioskName }, replace: true })
        return
      }
      router.navigate({ to: redirectTarget, replace: true })
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to log in. Please try again.'
      setErrorMessage(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    if (isAuthenticated && isLoginRoute) {
      router.navigate({ to: getRedirectTarget(), replace: true })
    }
  }, [isAuthenticated, isLoginRoute, router, location.search, location.pathname, user?.id])

  if (isAuthenticated) {
    return null
  }

  return (
    <LoginScreen
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      errorMessage={errorMessage}
      brandName={brand.name}
      title={isKioskLogin ? 'Log in to pair a kiosk tablet' : undefined}
      subtitle={isKioskLogin ? 'Use a normal staff account, then enter the 6-character code shown on the tablet.' : undefined}
      kioskShortcutHref="/kiosk"
      kioskShortcutLabel="Open kiosk"
    />
  )
}
